import api from "../axiosIntercepture";

/**
 * Fetch all pricing foundation records with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} pageSize - Number of records per page
 * @returns {Promise} - Returns pricing foundation records
 */
export const fetchPricingFoundationList = async (page = 0, pageSize = 10) => {
    try {
        const response = await api.get(`/pricingFoundation/list`, {
            params: { page: page + 1, limit: pageSize },
        });

        const payload = response.data;
        const result = payload?.results ?? payload?.data ?? payload;
        if (Array.isArray(result)) {
            return result;
        }
        return result?.items || result?.records || result?.results || result?.data || [];
    } catch (err) {
        throw err?.response?.data?.message || "Failed to fetch pricing foundation";
    }
};

/**
 * Fetch SKU IDs mapped to a customer/supplier.
 * @param {string} customerId - Customer ID
 * @returns {Promise<Array>} - Returns SKU records
 */
export const fetchPricingFoundationCustomerSkus = async (customerId) => {
    try {
        if (!customerId) return [];
        const response = await api.get(`/pricingFoundation/customer-skus/${customerId}`);
        const payload = response?.data;
        return (
            payload?.results ||
            payload?.data ||
            payload?.items ||
            payload?.records ||
            payload ||
            []
        );
    } catch (err) {
        throw err?.response?.data?.message || "Failed to fetch customer SKUs";
    }
};

/**
 * Create a new pricing foundation record
 * @param {object} data - Pricing foundation data
 * @returns {Promise} - Returns created record
 */
export const createPricingFoundation = async (data) => {
    try {
        const payload = {
            skuId: data.skuid,
            description: data.description,
            originalPrice: parseFloat(data.originalPrice),
            sellingPrice: parseFloat(data.sellingPrice),
            customer: data.customer,
            customerGroup: data.customerGroup || "",
        };
        
        const response = await api.post(
            `/pricingFoundation/create`,
            payload
        );
        return response.data?.results || response.data?.data;
    } catch (err) {
        throw err?.response?.data?.message || "Failed to create pricing foundation";
    }
};

/**
 * Update an existing pricing foundation record
 * @param {string} id - Record ID
 * @param {object} data - Updated pricing foundation data
 * @returns {Promise} - Returns updated record
 */
export const updatePricingFoundation = async (id, data) => {
    try {
        const payload = {
            skuId: data.skuid,
            description: data.description,
            originalPrice: parseFloat(data.originalPrice),
            sellingPrice: parseFloat(data.sellingPrice),
            customer: data.customer,
            customerGroup: data.customerGroup || "",
        };
        
        const response = await api.post(
            `/pricingFoundation/update/${id}`,
            payload
        );
        return response.data?.results || response.data?.data;
    } catch (err) {
        throw err?.response?.data?.message || "Failed to update pricing foundation";
    }
};

/**
 * Delete a pricing foundation record
 * @param {string} id - Record ID
 * @returns {Promise} - Returns success message
 */
export const deletePricingFoundation = async (id) => {
    try {
        const response = await api.post(
            `/pricingFoundation/delete/${id}`,
            {}
        );
        return response.data?.results || response.data?.data;
    } catch (err) {
        throw err?.response?.data?.message || "Failed to delete pricing foundation";
    }
};

/**
 * Block/Unblock a pricing foundation record
 * @param {string} id - Record ID
 * @param {boolean} isBlocked - Whether to block the record
 * @returns {Promise} - Returns updated record
 */
export const togglePricingFoundationBlock = async (id, isBlocked) => {
    try {
        const response = await api.post(
            `/pricingFoundation/toggle-block/${id}`,
            { isBlocked }
        );
        return response.data?.results || response.data?.data;
    } catch (err) {
        throw err?.response?.data?.message || "Failed to update pricing foundation status";
    }
};

/**
 * Search pricing foundation records
 * @param {object} searchParams - Search parameters
 * @returns {Promise} - Returns matching records
 */
export const searchPricingFoundation = async (searchParams) => {
    try {
        const response = await api.post(
            `/pricingFoundation/search`,
            searchParams
        );
        return response.data?.results || response.data?.data || [];
    } catch (err) {
        throw err?.response?.data?.message || "Failed to search pricing foundation";
    }
};
